
export enum Portal {
    LANDING = 'LANDING',
    ADMIN = 'ADMIN',
    TEACHER = 'TEACHER',
    STUDENT = 'STUDENT',
    NON_ACADEMIC = 'NON_ACADEMIC',
    EQA = 'EQA',
    CBT_STUDENT = 'CBT_STUDENT',
    CBT_TEACHER = 'CBT_TEACHER',
    SUPER_ADMIN = 'SUPER_ADMIN',
    EXTERNAL_STUDENT = 'EXTERNAL_STUDENT',
}

/** What kind of place this is. Decides which modules a new school starts with —
 *  see src/config/institutionPresets.ts. */
export type InstitutionType =
    | 'K-12 School'
    | 'Islamic Center'
    | 'Training Center'
    | 'Vocational Institution'
    | 'College/University'
    | 'Individual Online Tutor';

export type SchoolFeature =
    | 'presentations'
    | 'live_classes'
    | 'cbt'
    | 'cambridge'
    | 'lms'
    | 'ai_tutor'
    | 'tahfiz'
    | 'musabaqa'
    | 'finance'
    | 'iqa'
    | 'scholarships'
    | 'games'
    | 'analytics'
    | 'aicpd'
    | 'admissions';

export interface School {
    id: string;
    name: string;
    abbreviation?: string; // New field for dashboard branding
    logoUrl?: string;
    address?: string;
    contactEmail: string; // Made required as per user request
    contactPhone?: string;
    adminName: string; // New: Name of the school admin
    subscriptionStatus: 'active' | 'inactive' | 'suspended' | 'denied'; // Added 'denied'
    subscriptionPlan: 'monthly' | '1-year' | '2-year' | '5-year'; // Updated plans
    subscriptionFee?: number; // New field
    subscriptionExpiry: number;
    createdAt: number;
    maxStudents?: number;
    settings?: SchoolSettings;
    enabledFeatures?: SchoolFeature[]; // undefined = all features enabled (backward compat)
    // Cached Analytics (Optional, can be updated via cloud functions or periodically)
    studentCount?: number;
    teacherCount?: number;
    institutionType?: InstitutionType;
    customDomain?: string;
    subdomain?: string;
    // White-label subscription lifecycle
    whitelabelStatus?: 'pending_approval' | 'approved' | 'active' | 'suspended' | 'expired';
    whitelabelPlan?: 'subdomain' | 'professional' | 'enterprise';
    whitelabelStartedAt?: number;
    whitelabelExpiresAt?: number;
    whitelabelSuspendedAt?: number;
    whitelabelPaymentGateway?: 'stripe' | 'paystack' | 'flutterwave';
    whitelabelLastPaymentRef?: string;
    whitelabelApprovedAt?: number;
    whitelabelApprovedBy?: string;
    // School public profile (used on white-labeled landing page)
    aboutUs?: string;
    tagline?: string;
    bannerImageUrl?: string;
}

export type WhitelabelPlan = 'subdomain' | 'professional' | 'enterprise';
export type WhitelabelStatus = 'pending_approval' | 'approved' | 'active' | 'suspended' | 'expired';
export type PaymentGatewayType = 'stripe' | 'paystack' | 'flutterwave';

export interface SchoolPartnerApplication {
    id: string;
    schoolName: string;
    contactName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    institutionType: string;
    estimatedStudents: string;
    currentChallenges: string;
    interestedPlan: WhitelabelPlan | 'ess' | 'std' | 'pre' | 'trn';
    referralCode?: string;
    status: 'pending' | 'processing' | 'approved' | 'rejected';
    submittedAt: number;
    reviewedAt?: number;
    reviewedBy?: string;
    notes?: string;
    schoolId?: string;
    adminEmail?: string;
    adminUsername?: string;
    adminPassword?: string;
    logoUrl?: string;
    processingAt?: number;
    processingBy?: string;
}

export type AdminPermission =
    | 'manage_students'
    | 'manage_teachers'
    | 'manage_classes'
    | 'manage_finance'
    | 'manage_timetable'
    | 'manage_cbt'
    | 'manage_settings'
    | 'manage_admins'
    | 'manage_non_academic'
    | 'manage_iqa'
    | 'manage_observations';

export interface AdminAccount {
    id: string;
    username: string;
    email: string;
    password?: string;
    role: 'admin';
    permissions: AdminPermission[];
    fullName: string;
    active: boolean;
    schoolId: string; // Tenant ID
}

export interface SuperAdminAccount {
    id: string;
    username: string;
    email: string;
    role: 'super_admin';
    fullName: string;
}

export interface User {
    id: string;
    role: 'admin' | 'teacher' | 'student' | 'non-academic' | 'super_admin';
    data?: any;
    schoolId?: string; // Tenant ID (optional for super_admin)
    email?: string;
    permissions?: AdminPermission[]; // For admins
}

export interface TeacherAccount {
    id?: string;
    username: string;
    email: string; // Added for Firebase Auth mapping
    password?: string;
    authUid?: string; // The real Firebase Auth UID once created — may differ from `id` for
                       // teachers added directly (whose doc ID is just their username, not a UID)

    role: 'teacher';
    active: boolean;
    fullName: string;
    /** for the school's messages by SMS or WhatsApp */
    phone?: string;
    /** where the salary is paid (for the bank's bulk-payment schedule) */
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    schoolId: string; // Tenant ID
    sectionIds: string[];
    photoDataUrl?: string;
    signatureDataUrl?: string;
    qualification?: string;
    salary?: {
        basic: number;
        allowances: { name: string; amount: number }[];
        deductions: { name: string; amount: number }[];
    };
    subjects?: { classId: string; subject: string }[];
    // Denormalized, server-maintained mirror of subjects[].classId — see the matching
    // comment on Student.enrolledClassIds. Never set directly by client code.
    subjectClassIds?: string[];

    // LMS Fields
    lmsEnabled?: boolean;
    bio?: string;
    rating?: number;
    totalStudents?: number;
    totalEarnings?: number;
    experienceLevel?: 'Experienced' | 'NQT';
}

// --- LMS TYPES ---
export interface LMSCourse {
    id: string;
    title: string;
    slug: string;
    description: string;
    thumbnailUrl?: string; // Featured Image
    introVideoUrl?: string;
    instructorId: string; // Teacher Username/ID
    instructorName: string;
    schoolId: string; // Tenant ID

    level: 'Beginner' | 'Intermediate' | 'Expert' | 'All Levels';
    category: string;
    subject?: string; // Cambridge subject when category is 'K-12 Subjects'
    tags: string[];

    isFree: boolean;
    price: number;
    currencies?: Record<string, number>; // e.g. { NGN: 15000, USD: 10, GBP: 8 }
    deliveryType?: 'recorded' | 'live' | 'hybrid';
    liveClassroomId?: string;
    duration?: string; // e.g. "10h 30m"

    status: 'draft' | 'pending' | 'published' | 'archived';
    createdAt: number;
    updatedAt: number;

    totalEnrolled: number;
    rating: number;
    reviewCount: number;

    // New Features
    prerequisites?: string[]; // IDs of courses required
    certificateTemplateId?: string;
    enableDripContent?: boolean;
    modules: LMSModule[];

    // Assignment
    isCPD?: boolean; // If true, appears in Teacher CPD dashboard
    targetClassIds?: string[]; // If set, appears for students in these classes (if not public)

    // Certificate Settings
    certificateSettings?: {
        enabled: boolean;
        style: 'classic' | 'modern' | 'tech' | 'elegant' | 'report-card';
        signerName?: string;
        signatureUrl?: string;
    };
}

export interface LMSBundle {
    id: string;
    title: string;
    description: string;
    courseIds: string[];
    price: number;
    thumbnailUrl?: string;
    schoolId: string;
    status: 'draft' | 'published';
    type: 'Diploma' | 'Certification' | 'Bundle';
    createdAt: number;
    updatedAt: number;
}

export interface LMSModule {
    id: string; // unique ID
    title: string;
    lessons: LMSLesson[];
    unlockDaysAfterEnrollment?: number; // Drip content
    thumbnailUrl?: string; // Module Featured Image
}

export interface LMSQuestion {
    id: string;
    text: string;
    type: 'multiple_choice' | 'multiple_response' | 'true_false' | 'yes_no' | 'short_answer' | 'essay';
    options?: string[]; // For MC/TF/YesNo/MultipleResponse
    correctAnswer: string; // Used for single-answer types
    correctAnswers?: string[]; // Used for multiple_response
    modelAnswer?: string; // Used for Essay/Short Answer AI grading
    points: number;
    mediaUrl?: string; // Image/Video/Audio for the question
    mediaType?: 'image' | 'video' | 'audio';
    explanation?: string; // Explanation for the correct answer
}

export interface LMSQuizData {
    questions: LMSQuestion[];
    passingScore: number;
    timeLimitMinutes?: number;
    maxAttempts?: number;
}

export interface LMSAssignmentData {
    instructions: string;
    maxScore: number;
    dueDate?: number;
}

export interface LMSLesson {
    id: string;
    title: string;
    type: 'video' | 'text' | 'quiz' | 'assignment' | 'live';
    content?: string; // Text content or URL
    videoUrl?: string; // Supports YouTube/Vimeo embed
    featuredImage?: string; // Lesson Featured Image
    audioUrl?: string; // Audio attachment (Podcast/Voice note)
    attachments?: { name: string; url: string }[];
    durationSeconds?: number;
    isPreview: boolean; // Free preview
    isPrerequisite?: boolean; // Must complete to move forward
    quizData?: LMSQuizData;
    assignmentData?: LMSAssignmentData;
    liveData?: {
        date: string;
        time: string;
        meetingUrl?: string;
        durationMinutes?: number;
        liveSessionId?: string; // ID of internal live_sessions doc
        useInternalClassroom?: boolean;
    };
}

export interface LMSEnrollment {
    id: string;
    studentId: string;
    courseId: string;
    enrolledAt: number;
    progress: number; // 0-100
    completedLessonIds: string[];
    lastAccessedAt: number;
    lastAccessedLessonId?: string;
    // Payment fields
    paymentStatus?: 'free' | 'paid' | 'pending_bank' | 'pending_gateway';
    paymentAmount?: number;
    paymentCurrency?: string;
    paymentRef?: string;
    paymentMethod?: string; // 'paystack' | 'flutterwave' | 'stripe' | 'bank_transfer' | 'free'
    schoolId?: string;
    courseTitle?: string;
    instructorId?: string;
    studentName?: string;
    studentEmail?: string;
}

export interface LMSWithdrawalRequest {
    id: string;
    instructorId: string;
    instructorName: string;
    amount: number;
    method: string; // e.g. 'Bank Transfer', 'PayPal'
    details: string; // Account Number, etc.
    status: 'pending' | 'approved' | 'rejected';
    requestedAt: number;
    processedAt?: number;
    schoolId: string;
}

export interface LMSCategory {
    id: string;
    name: string;
    slug: string;
    count?: number; // Course count
    schoolId: string;
}

export interface ClassEnrollment {
    sectionId: string;
    classId: string;
}

export interface SectionRecord {
    teacherComment: string;
    headComment: string;
    extracurricularActivities: { activity: string; remark: string }[];
}

export interface Student {
    id: string;
    email: string; // Added for Firebase Auth mapping
    enrollments: ClassEnrollment[];
    // Denormalized, server-maintained mirror of enrollments[].classId/sectionId — Firestore
    // rules can't loop over an array of maps, so a teacher-scoping rule needs these flat
    // arrays to check with hasAny(). Kept in sync by functions/src/index.ts's
    // onDocumentWritten trigger; never set directly by client code.
    enrolledClassIds?: string[];
    enrolledSectionIds?: string[];
    firstName: string;
    fullName: string;
    schoolId: string; // Tenant ID
    surname: string;
    nin: string;
    reg: string;
    photoDataUrl?: string;
    subjects: Subject[];
    teacherComment: string;
    headComment: string;
    status: 'active' | 'transferred' | 'graduated' | 'inactive';
    transferredFrom?: string;
    session: string;
    dateOfBirth: string;
    gender: 'Male' | 'Female';
    extracurricularActivities?: { activity: string; remark: string }[];
    sectionRecords?: { [classId: string]: SectionRecord }; // Added for per-section isolation
    scholarship?: {
        type: 'Merit' | 'TeachersChild' | 'Orphanage' | 'SingleMother' | 'Family';
        active: boolean;
        awardedAt?: number;
        atRisk?: boolean; // Below the type's required average; flagged for admin review, not auto-stripped
        atRiskReason?: 'below_average';
        strippedReason?: 'repeated_class';
        strippedAt?: number;
    };
    isExternal?: boolean; // For students manually enrolled by Tutors
    // Set by admin on approving a TransferRequest; cleared implicitly once the student is
    // archived and this doc is deleted by the checkTransferDeadlines scheduled function.
    transferStatus?: 'pending' | 'approved';
    activeTransferRequestId?: string;
    transferDeadline?: number; // approval timestamp + 5 working days; drives auto-archival
    // Parent & Guardian details
    parentName?: string;
    parentPhone?: string;
    parentEmail?: string;
    guardianName?: string;
    guardianPhone?: string;
    fatherName?: string;
    emergencyContact?: string;
    /** the language the school writes to the parent in (English, Hausa, Arabic, Yoruba, Igbo or French) */
    parentLanguage?: string;
    /** the parent agreed to receive the school's messages by email, SMS or WhatsApp */
    parentContactConsent?: boolean;
}

// A student's (on behalf of their parent — there is no separate Parent Portal) request to
// leave the school. Mirrors the reviews collection's status/approval shape. Approval sets
// deadline (5 working days out) on both this doc and the student doc; the student's
// dashboard locks down to records-only until they download their documents or the
// checkTransferDeadlines scheduled function archives and removes them at the deadline.
export interface TransferRequest {
    id: string;
    schoolId: string;
    studentId: string;
    studentName: string;
    reg: string;
    classId: string;
    className: string;
    reason: string;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    requestedAt: number;
    reviewedAt?: number;
    reviewedBy?: string;
    adminComment?: string;
    conductRemark?: string;
    deadline?: number;
    transcriptDownloadedAt?: number;
    certificateDownloadedAt?: number;
    archivedAt?: number;
}

// A frozen snapshot of a student's full record at the moment they're removed from the
// active system — written by checkTransferDeadlines just before the live students/{id}
// doc is deleted. Kept for admin search/reference; never surfaced to students/teachers.
export interface ArchivedStudent extends Student {
    archivedAt: number;
    transferReason: string;
    conductRemark?: string;
    lastClassName: string;
    transferApprovedAt?: number;
}

// Admin-configured catalog of Summer offerings — remedial (tied to a resit subject)
// or general enrichment electives (Robotics, Smart Kids Farming, etc.)
export interface SummerProgram {
    id: string;
    schoolId: string;
    name: string;
    description?: string;
    objectives?: string;
    startDate?: string; // ISO date string, e.g. '2026-07-20'
    endDate?: string;
    thumbnailUrl?: string; // Firebase Storage URL (not a data URI — needed for og:image link previews)
    fee: number;
    linkedResitSubject?: string; // e.g. 'Cambridge Mathematics' — omit for general electives
    active: boolean;
}

// A specific student's enrollment in a Summer Program, with its own invoice.
export interface SummerEnrollment {
    id: string;
    schoolId: string;
    studentId: string;
    programId: string;
    programName: string;
    fee: number;
    term: number;
    session: string;
    offeredAt: number;
    accepted?: boolean;
    invoiceId?: string;
}

// A public, no-login signup for a Summer Program — filled via a shareable link by
// either an existing student or someone entirely outside the institution. A parent
// registering several children in one visit produces one doc per child, all sharing
// `groupId` and the same payer contact + payment record, so they can be verified together.
export interface SummerProgramSignup {
    id: string;
    schoolId: string;
    programId: string;
    programName: string;
    fee: number; // per participant
    groupId: string;
    payerName: string;
    payerEmail: string;
    payerPhone: string;
    participantName: string;
    isExistingStudent: boolean;
    classId?: string;
    className?: string;
    paymentMethod: 'paystack' | 'stripe' | 'bank' | 'none';
    paymentRef?: string;
    receiptUrl?: string;
    paymentVerified: boolean;
    createdAt: number;
}

export interface Subject {
    id: string;
    name: string;
    ca1: number;
    ca2: number;
    exam: number;
    classId?: string; // Added to link subject to a specific class/section
    term?: number; // Added to distinguish between terms (1, 2, 3)
    resitStatus?: 'scheduled' | 'passed' | 'failed' | 'missed';
    resitScheduledAt?: number; // Date/time the resit sitting is set for
    resitScheduledBy?: string; // uid/username of whoever scheduled it
    resitScheduledByName?: string;
    resitScore?: number; // The exam score entered once the resit has been recorded
    resitRecordedAt?: number;
    resitRecordedBy?: string; // uid/username of whoever entered the resit score (teacher, or admin for a direct record)
    resitRecordedByName?: string;
    resitApprovedBy?: string; // admin who approved the review carrying this resit score (teacher-submitted flow only)
    resitApprovedByName?: string;
    resitApprovedAt?: number;
    resitMissedActionAt?: number; // when a missed-resit consequence (repeat class / scholarship strip) was applied
    resitMissedActionBy?: string; // admin who applied it
    resitMissedActionByName?: string;
    originalExamBeforeResit?: number; // Kept for transparency after a resit overwrites `exam`
}

export interface Section {
    id: string;
    schoolId: string; // Tenant ID
    name: string;
}

export type CurriculumType = 'conventional' | 'islamiyya' | 'tahfiz' | 'early-years' | 'islamiyya-early' | 'none';

export interface SchoolClass {
    id: string;
    schoolId: string; // Tenant ID
    name: string;
    sectionId: string;
    teacherId?: string;
    feeTemplateIds?: string[];
    defaultFeeTemplateId?: string;
    curriculumType?: CurriculumType;
    // Denormalized, server-maintained roster of enrolled student ids — lets a
    // teacher's client resolve "which students are in my classes" via a
    // documentId()-based query instead of an array-contains-any query, which
    // firestore.rules can't validate per-document (see teacherCoversStudent()'s
    // comment). Kept in sync by functions/src/index.ts's syncStudentEnrollmentIndex.
    studentIds?: string[];
}

export interface TeacherProfile {
    name: string;
    phone: string;
    email: string;
    dept: string;
    school: string;
    session: string;
}

export interface TeacherData {
    profile: TeacherProfile;
    classes: SchoolClass[];
}

export interface Review {
    id: string;
    teacher: string;
    profile: TeacherProfile;
    className: string;
    classId?: string; // Added for robust matching
    sectionId: string;
    term: number;
    students: Student[];
    createdAt: number;
    status: 'pending' | 'approved' | 'rejected';
    adminComment?: string;
}

export interface Archive extends Review {
    approvedAt: number;
    schoolId: string;
    logo?: string;
    adminSignature?: string;
}

export interface PublishedResult {
    id: string;
    studentName: string;
    surname: string;
    nin: string;
    fullName: string;
    reg: string;
    photoDataUrl?: string;
    sectionName: string;
    sectionId?: string; // Added for robust matching (optional)
    className: string;
    classId?: string; // Added for robust matching (optional)
    term: number;
    school: string;
    schoolId: string; // Added to ensure correct filtering in App.tsx
    session: string;
    teacherName: string;
    teacherDept: string;
    subjects: (Subject & { total: number; grade: string; remark: string })[];
    teacherComment: string;
    headComment: string;
    adminComment: string;
    adminSignature: string;
    approvedAt: number;
    publishedAt?: number;
    archived: boolean;
    teacher: string;
    teacherSignatureUrl?: string;
    headOfSchoolSignatureUrl?: string;
    schoolStampUrl?: string;
    extracurricularActivities?: { activity: string; remark: string }[];
    attendanceSummary?: {
        rate: string;
        summary: string;
        breakdown: {
            early: number;
            onTime: number;
            late: number;
            absent: number;
        }
    };
    studentId: string;
}

export interface Archive extends Review {
    logo?: string;
    adminSignature?: string;
}

export enum IQAFormType {
    CURRICULUM_COVERAGE = 'Curriculum Coverage Tracker',
    WEEKLY_LOG = 'Weekly Curriculum Log',
    FORMATIVE_ASSESSMENT = 'Weekly Formative Assessment',
    DUTY_REPORT = 'Daily Duty Report',
    LESSON_NOTE = 'Lesson Note Generator',
}

export type CurriculumCoverageData = {
    classId: string;
    subject: string;
    term: number;
    weeks: { week: number; topic: string; subTopic: string; status: 'Covered' | 'Partially Covered' | 'Not Covered'; reflection: string; }[];
};

export type WeeklyLogEntry = {
    subject: string;
    textbook: string;
    classwork: string;
    homework: string;
    projectStatus: string;
};

export type WeeklyLogData = {
    classId: string;
    week: number;
    term: number;
    days: {
        day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday';
        date: string;
        entries: WeeklyLogEntry[];
    }[];
};

export type FormativeAssessmentData = {
    classId: string;
    week: number;
    term: number;
    subjects: { name: string; learningObjectives: string; assessmentMethod: string; metObjectives: string[]; needsSupport: string[]; interventionPlan: string; }[];
};

export type DutyReportData = {
    dutyArea: string;
    observationTime: string;
    observations: string;
    incidents: string;
    actionTaken: string;
    followUpRequired: boolean;
};


export interface IQASubmission {
    id: string;
    teacherId: string;
    formType: IQAFormType;
    formData: CurriculumCoverageData | WeeklyLogData | FormativeAssessmentData | DutyReportData | any;
    submittedAt: number;
    status: 'pending' | 'approved' | 'rejected';
    adminComment?: string;
    // Metadata for display
    title: string; // e.g., "Weekly Log: JSS 1A, Week 5"
}

export enum AttendanceStatus {
    PRESENT_EARLY = 'Early',
    PRESENT_ON_TIME = 'On Time',
    LATE_MINOR = 'Late (<15m)',
    LATE_MAJOR = 'Late (>15m)',
    ABSENT = 'Absent',
    EXCUSED = 'Excused',
}

export interface AttendanceSubmission {
    id: string;
    teacherId: string;
    classId: string;
    week: number;
    term: number;
    session: string;
    submittedAt: number;
    status: 'pending' | 'approved' | 'rejected';
    adminComment?: string;
    attendanceData: {
        [studentId: string]: {
            monday?: AttendanceStatus;
            tuesday?: AttendanceStatus;
            wednesday?: AttendanceStatus;
            thursday?: AttendanceStatus;
            friday?: AttendanceStatus;
            saturday?: AttendanceStatus;
            sunday?: AttendanceStatus;
            [day: string]: AttendanceStatus | undefined;
        }
    };
}

export interface Announcement {
    id: string;
    title: string;
    content: string;
    target: {
        type: 'all' | 'teachers' | 'students' | 'class';
        classIds?: string[];
    };
    createdAt: number;
}


// --- FINANCE MODULE TYPES ---

export interface FeeCategory {
    id: string;
    name: string;
    description?: string;
    defaultAmount?: number;
}

export interface InvoiceItem {
    categoryId: string; // Links to FeeCategory
    description: string;
    amount: number;
}

export interface Invoice {
    id: string;
    studentId: string;
    /** 1, 2 or 3 (older invoices hold the words, e.g. "First Term": read it with termNumber()) */
    term: number | string;
    session: string;
    items: InvoiceItem[];
    total: number;
    amountPaid: number;
    status: 'Paid' | 'Pending' | 'Overdue' | 'Verifying' | 'Scholarship' | 'Partially Paid';
    createdAt: number;
    /** older invoices hold an ISO date string: read it with dueMs() */
    dueDate: number | string;
    templateIds?: string[];
    proofOfPaymentUrl?: string;
    discount?: {
        type: 'percentage' | 'fixed';
        value: number;
        name?: string;
    };
    schoolId?: string;
    /** cancelled (raised by mistake): kept on record, not owed */
    void?: boolean;
    voidReason?: string;
    voidedAt?: number;
    voidedBy?: string;
    lastPaymentAt?: number;
    createdBy?: string;
}

export interface Payment {
    id: string;
    invoiceId: string;
    studentId: string;
    amount: number;
    method: 'Cash' | 'Bank Transfer' | 'POS' | 'Cheque' | 'Online' | 'Stripe' | 'Paystack' | 'Flutterwave' | 'Admin Verification';
    paidAt: number;
    paymentRef: string;
    schoolId?: string;
    /** the school's own receipt number, e.g. KCA/2026/000123 (payments since the finance update) */
    receiptNo?: string;
    receivedBy?: string;
    receivedByName?: string;
    createdAt?: number;
    source?: 'office' | 'transfer' | 'online';
    /** a payment entered by mistake is reversed, never deleted */
    status?: 'valid' | 'reversed';
    reversedAt?: number;
    reversedBy?: string;
    reversedByName?: string;
    reversalReason?: string;
    claimId?: string;
    note?: string;
}

export interface Payroll {
    id: string;
    teacherId: string; // Can be teacher or non-academic staff username
    month: number;
    year: number;
    basic: number;
    allowances: { name: string; amount: number }[];
    deductions: { name: string; amount: number }[];
    netPay: number;
    status: 'Draft' | 'Pending Confirmation' | 'Confirmed' | 'Paid';
    generatedAt: number;
    confirmedAt?: number;
    paidAt?: number;
    paymentDate?: number; // Kept for backward compatibility
    teacherSignature?: string;
}

export interface ExpenseClaim {
    id: string;
    teacherId: string;
    date: number;
    amount: number;
    description: string;
    receiptUrl?: string; // Base64 encoded image
    status: 'Pending' | 'Approved' | 'Rejected' | 'Paid';
    adminComment?: string;
}

export interface FeeTemplate {
    id: string;
    name: string;
    items: { categoryId: string; amount: number }[];
}

export interface Discount {
    id: string;
    name: string;
    type: 'percentage' | 'fixed';
    value: number;
}

export interface BankAccount {
    id: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
}

// A single row of a term/holiday calendar table, e.g. "1st Term | 6 Sep 2026 | 18 Dec
// 2026 | Academic | 15 Weeks" — dates are kept as free-text (matching how regulators like
// KSSQAA publish them, e.g. "Sunday 6th September 2026") rather than parsed timestamps.
export interface SchoolCalendarRow {
    label: string;
    from: string;
    to: string;
    duration: string;
    weeks: string;
}

// One printable table block within the calendar — a school year normally has one, plus
// an optional second one previewing the following session's first term (as KSSQAA does).
export interface SchoolCalendarSession {
    session: string;
    heading: string;
    rows: SchoolCalendarRow[];
}

// The school's official term-dates calendar, typically sourced from a state/regulatory
// authority notice — one document per school, admin-editable, read-only for teachers/students.
export interface SchoolCalendar {
    schoolId: string;
    documentTitle: string;
    documentSubtitle?: string;
    authorityName?: string;
    refNumber?: string;
    issueDate?: string;
    motto?: string;
    sessions: SchoolCalendarSession[];
    notes: string[];
    signatoryName?: string;
    signatoryTitle?: string;
    updatedAt: number;
    updatedBy?: string;
}

// One extracurricular activity slotted against a single curriculum track for a single
// term — the school's own calendar deliberately allows only ONE activity per
// term/track (not a free list), matching how it's actually planned.
export type CurriculumTrack = 'conventional' | 'islamiyya' | 'tahfiz';

export interface CurriculumActivitySlot {
    activityName: string;
    date?: string; // yyyy-mm-dd
    venue?: string;
}

export interface TermActivities {
    term: number; // 1, 2, or 3
    conventional?: CurriculumActivitySlot;
    islamiyya?: CurriculumActivitySlot;
    tahfiz?: CurriculumActivitySlot;
}

// The school's own extracurricular activities calendar (distinct from SchoolCalendar,
// which mirrors an external regulator's term-dates notice) — one document per
// schoolId+session, admin-editable, read-only for teachers/students.
export interface SchoolActivityCalendar {
    schoolId: string;
    session: string;
    terms: TermActivities[];
    updatedAt: number;
    updatedBy?: string;
}

// Weekly Scheme of Work + generated lesson materials — an operational, calendar-aligned
// teaching plan (distinct from the static reference curricula in
// src/components/portals/curriculum/, which are read-only syllabus overviews with no
// week-by-week breakdown or generation tooling). One SchemeOfWork per curriculum level
// per term; one WeeklyLessonMaterials per level+week+subject.
// Bloom's Taxonomy learning domains — every generated week/lesson is classified by which
// domain(s) its objectives primarily target, so pedagogy (not just content) is intentional.
export type LearningDomain = 'Cognitive' | 'Affective' | 'Psychomotor';

export interface SchemeOfWorkWeekSubject {
    topic: string;
    objectives: string[];
    domains?: LearningDomain[]; // Bloom's domain(s) this week's objectives target
}

export interface SchemeOfWorkWeek {
    weekNumber: number;
    startDate: string; // YYYY-MM-DD, derived from the school's Official Calendar
    endDate: string;   // YYYY-MM-DD
    subjects: Record<string, SchemeOfWorkWeekSubject>; // keyed by subject key (e.g. IslamiyyaEarlySubjectKey)
}

export interface SchemeOfWork {
    schoolId: string;
    levelSlug: string;   // e.g. 'hadana' | 'rauda-1' | 'rauda-2'
    levelLabel: string;  // e.g. 'Hadana'
    classIds: string[];  // real SchoolClass id(s) this plan applies to
    curriculumType: CurriculumType;
    session: string;
    term: number;
    weeks: SchemeOfWorkWeek[];
    calendarSourceHash?: string; // detects when the Official Calendar's term dates changed after generation
    generatedAt?: number;
    generatedBy?: string;
    updatedAt: number;
    updatedBy?: string;
}

export interface WorksheetItem {
    prompt: string;
    options?: string[];
    answer?: string;
}

export interface WorksheetSection {
    title: string;
    type: 'fill_in_blank' | 'matching' | 'short_answer' | 'tracing' | 'coloring_prompt';
    instructions?: string;
    items: WorksheetItem[];
}

export interface WorksheetContent {
    title: string;
    subject: string;
    weekLabel: string;
    sections: WorksheetSection[];
}

export interface WeeklyLessonMaterials {
    schoolId: string;
    levelSlug: string;
    classIds: string[];
    curriculumType: CurriculumType;
    subjectKey: string;
    subjectName: string;
    session: string;
    term: number;
    weekNumber: number;
    topic: string;
    domains?: LearningDomain[]; // Bloom's domain(s), carried over from the Scheme of Work week
    lessonNote?: { content: string; generatedAt: number; generatedBy: string };
    lessonPlan?: { content: string; generatedAt: number; generatedBy: string };
    worksheet?: { content: WorksheetContent; generatedAt: number; generatedBy: string };
    presentationDeckId?: string;
    updatedAt: number;
    updatedBy?: string;
}

export interface SchoolSettings {
    schoolName: string;
    schoolAbbreviation?: string;
    logoDataUrl?: string;
    headOfSchoolSignatureUrl?: string;
    schoolStampUrl?: string;
    partnerLogos?: string[]; // New field for accreditation logos
    schoolAddress?: string;
    schoolEmail?: string;
    schoolPhone?: string;
    // DEPRECATED but kept for migration
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    // NEW
    bankAccounts?: BankAccount[];
    paymentGateways?: PaymentGateways;
    payrollSettings?: {
        payDay: number; // e.g., 25
        globalDeductions: {
            id: string;
            name: string;
            amount: number;
            type: 'fixed' | 'percentage';
        }[];
    };
    lmsRevenueShare?: { teacherPercent: number; schoolPercent: number };
}

export interface PaymentGateways {
    stripe: { publicKey: string; secretKey: string; enabled: boolean; ngnRate?: number };
    paystack: { publicKey: string; secretKey: string; enabled: boolean };
    flutterwave: { publicKey: string; secretKey: string; enabled: boolean };
    remita: { publicKey: string; secretKey: string; enabled: boolean };
}

export type SalaryAdvanceStatus = 'Pending' | 'Approved' | 'Rejected' | 'Paid' | 'Repaying' | 'Completed';

export interface RepaymentPlan {
    approvedAmount: number;
    startDate: number; // timestamp
    method: 'full' | 'installments';
    installments?: number; // number of months
    monthlyDeduction?: number;
}

export interface SalaryAdvanceRequest {
    id: string;
    teacherId: string; // Can be teacher or non-academic staff username
    amount: number;
    reason: string;
    requestDate: number; // timestamp
    status: SalaryAdvanceStatus;
    repaymentPlan?: RepaymentPlan;
    adminComment?: string; // for rejection
    paidDate?: number;
    repaidAmount: number;
}

// --- NON-ACADEMIC STAFF TYPES ---
export type Department = 'Maintenance' | 'Cleaning' | 'Security' | 'IT Support' | 'Receptionist' | 'Director' | 'Asst. Director' | 'Secretary' | 'Accountant' | 'HOS' | 'Staff' | 'Coach' | 'Trainer' | 'Chef' | 'Lawyer' | 'Nurse' | 'Casual Staff' | 'Camera man' | 'Social Media Manager';

export interface NonAcademicStaffAccount {
    username: string;
    schoolId: string; // Tenant ID
    email: string; // Added for Firebase Auth mapping
    password?: string;
    role: 'non-academic';
    active: boolean;
    fullName: string;
    department: Department;
    photoDataUrl?: string;
    signatureDataUrl?: string;
    salary?: {
        basic: number;
        allowances: { name: string; amount: number }[];
        deductions: { name: string; amount: number }[];
    };
}

export interface ClockInRecord {
    id: string;
    staffId: string; // username of non-academic staff or teacher
    staffName: string;
    role: 'teacher' | 'non-academic';
    department?: string; // for Islamiyya check or Non-Academic dept
    clockInTime: number;
    clockOutTime?: number;
}

export type IssueUrgency = 'Low' | 'Medium' | 'High';
export type IssueStatus = 'Reported' | 'In Progress' | 'Resolved' | 'Closed';
export type IssueCategory = 'Maintenance' | 'Cleaning' | 'Security' | 'IT Issue' | 'Other';

export interface IssueReport {
    id: string;
    reportedById: string; // username of reporter
    category: IssueCategory;
    location: string;
    urgency: IssueUrgency;
    description: string;
    photoDataUrl?: string;
    reportedAt: number;
    status: IssueStatus;
    assignedToId?: string; // username of assigned staff
    adminNotes?: string;
}

// --- IQA SAMPLING PLAN TYPES ---
export type SampleType = 'Interim' | 'Summative' | 'Interim & Summative' | string;
export type RiskRating = 'High' | 'Medium' | 'Low';
export type SamplingStatus = 'Pending' | 'Complete' | 'Action Required';

export interface IQASamplingPlanEntry {
    id: string;
    assessorId: string; // Teacher's username
    learnerId?: string; // Student's ID (Optional for general plans) - DEPRECATED in favor of learnerIds for new plans
    learnerIds?: string[]; // List of selected students for sampling
    qualificationUnit: string; // Keeps compatibility, or used for "Subject"
    subject?: string; // New field for manual subject entry
    unitRange?: string; // e.g. "1-5"
    samplingItems?: string[]; // e.g. ["Marking", "Exams"]

    sampleType: string; // Changed to string to support "Interim, Summative"
    riskRating: RiskRating; // Traffic Light: High=Red, Medium=Amber, Low=Green
    plannedDate: number; // timestamp (Start Date)
    plannedEndDate?: number; // timestamp (End Date)
    status: SamplingStatus;
    evidenceLink?: string;
    feedback?: string;
    week?: string; // e.g. "1-2"
    rationale?: string; // e.g. "Assessor/Methods: New staff member..."
    sampleRate?: string; // e.g. "100% Planning"

    // Existing fields inferred from usage
    schoolId: string; // Tenant ID
    cycle?: string;
    plannedActivities?: string[];
}

// --- CLASSROOM OBSERVATION TYPES ---
export interface DetailedObservationScore {
    // Structure: category_criterion (e.g., prep_a, teach_b)
    [key: string]: number; // 0-4
}

export interface ObservationScore {
    // Legacy Support
    preparation: number;
    teachingQuality: number;
    assessment: number;
    classroomManagement: number;
    subjectKnowledge: number;
}

export type ObservationStatus = 'Scheduled' | 'Completed';
export type ObservationType = 'Formal' | 'Informal' | 'Peer' | 'Learning Walk';
export type ObservationStage = 'Scheduled' | 'Pre-Observation' | 'Observation' | 'Post-Observation' | 'Completed';

export interface Observation {
    id: string;
    teacherId: string;
    classId: string;
    date: number;
    stage: ObservationStage;
    observationType: ObservationType;
    mode: 'physical' | 'virtual'; // Added for Virtual Observation
    meetingLink?: string;
    preObservationNotes?: string;
    postObservationNotes?: string;
    strengths?: string;
    improvements?: string;
    status: 'Scheduled' | 'Completed' | 'Cancelled';
    rubric?: {
        [key: string]: { score: number; max: number; notes: string };
    };
    professionalism?: {
        dressCode?: number;
        ethics?: number;
        timeManagement?: number;
        communication?: number;
    };
}

// --- ACTION PLAN TYPES ---
export type ActionPlanStatus = 'Open' | 'Pending Review' | 'Closed';

export interface ActionPlanItemComment {
    authorId: string; // username
    authorName: string;
    text: string;
    createdAt: number;
}

export interface TeacherPerformanceReport {
    id: string;
    teacherId: string;
    schoolId: string;
    session: string;
    term: number;
    overallAverage: number;
    totalTeachers: number;
    rank: number;
    subjectStats: {
        name: string;
        average: number;
        passRate: number;
        studentCount: number;
        status: 'Exceeding' | 'Meeting' | 'Below' | 'Critical';
    }[];
    strengths: string[];
    improvements: string[];
    headOfSchoolFeedback: string;
    includedResultIds: string[]; // List of PublishedResult IDs used in calculation
    sectionRank?: number;
    totalInSection?: number;
    sectionName?: string;
    publishedAt: number;
    updatedAt: number;
}

export interface ActionPlanItem {
    id: string;
    teacherId: string; // Teacher's username
    title: string;
    description: string;
    source: {
        type: 'IQA Submission' | 'Observation' | 'Manual';
        id: string; // ID of the review, observation, etc.
        text: string; // e.g., "Observation for JSS1 on 2024-08-15"
    };
    createdBy: string; // Admin's username
    createdAt: number;
    dueDate: number;
    status: ActionPlanStatus;
    comments: ActionPlanItemComment[];
    closedAt?: number;
    closedBy?: string; // Admin's username
}

// --- CPD LOG TYPES ---
export interface CPDLog {
    id: string;
    teacherId: string;
    fileUrl?: string;
    description: string;
    date: number;
    type: 'Certificate' | 'Reflection';
}

// --- STANDARDISATION HUB TYPES ---
export type StandardisationFileType = 'Meeting Minutes' | 'Benchmark Examples' | 'Meeting Agenda';

export interface StandardisationFile {
    id: string;
    fileName: string;
    fileType: StandardisationFileType;
    fileDataUrl: string; // Base64 encoded file
    uploadedAt: number;
    uploadedBy: string; // Admin's username
    description?: string;
}

export interface StandardizationCaseStudy {
    subject: string;
    evidenceType: string;
    proposedGrade: string;
    agreedGrade: string;
    rationale: string;
    gradingDifferences?: string; // Notes on why grades differed
}

export interface StandardizationMeeting {
    id: string; // Firestore Doc ID
    meetingId: string; // e.g. STD-2025-T2-01
    title: string; // defaults to Meeting ID or Focus Area
    date: number;
    startTime: string;
    leadIqa: string;
    focusArea: string;
    meetingLink?: string;
    description?: string;

    // Agenda & Objectives
    previousActions?: string;
    objectives: string[];
    resourceCheck: boolean; // Confirmed teachers have specs/briefs

    // Case Study - The Borderline Sample
    caseStudies: StandardizationCaseStudy[];

    // VARCS Compliance Check
    varcs: {
        valid: boolean;
        authentic: boolean;
        reliable: boolean;
        current: boolean;
        sufficient: boolean;
    };

    // Dashboard KPIs (Snapshot at time of meeting)
    kpis?: {
        regularity: number; // %
        agreementRate: number; // %
        actionResolution: number; // %
    };

    createdBy: string; // Admin username
    createdAt: number;

    // New Fields
    sections?: string[]; // e.g. ['Early Years', 'Primary', 'Secondary']
    participatingTeachers?: string[]; // IDs of teachers selected to participate
    meetingType?: 'Virtual' | 'Face to Face';
}

// --- STUDENT FEEDBACK TYPES ---
export type FeedbackType = 'General Feedback' | 'Appeal' | 'Complaint' | 'Appreciation';
export type FeedbackStatus = 'Submitted' | 'Under Review' | 'Resolved' | 'Closed';

export interface StudentFeedback {
    id: string;
    studentId: string;
    studentName: string; // For easy display
    type: FeedbackType;
    subject: string;
    message: string;
    submittedAt: number;
    status: FeedbackStatus;
    adminResponse?: string;
    resolvedAt?: number;
}


// --- AI INSIGHTS ---
export interface AIAnalysisResult {
    teamStrengths: string[];
    developmentAreas: string[];
    prioritySupport: { teacherName: string; reason: string }[];
}

// --- CBT MODULE TYPES ---
export interface Question {
    id: string;
    text: string;
    type: 'single_choice' | 'multiple_choice' | 'multi_choice' | 'multiple_response' | 'true_false' | 'yes_no' | 'short_answer' | 'essay';
    options: string[];
    correctAnswer?: string; // For single answer types
    correctAnswers: string[];
    modelAnswer?: string; // For AI grading
    points: number;
    imageDataUrl?: string;
    audioDataUrl?: string;
    videoUrl?: string; // YouTube/Vimeo URL
    essayMaxLength?: number;
    optionMedia?: string[]; // Array of image URLs corresponding to options
    isPreview?: boolean; // UI state for editor
}

export interface CBT_Test {
    id: string;
    title: string;
    subject: string;
    classId: string;
    teacherId: string;
    schoolId?: string; // Tenant ID
    durationMinutes: number;
    startDate?: number;
    endDate?: number;
    pointsPerQuestion: number;
    status: 'draft' | 'published' | 'archived';
    createdAt: number;
    // Proctoring Settings
    proctoringSettings?: {
        enabled: boolean;
        enableAudio?: boolean; // Can examiner speak?
        enableAi?: boolean; // Auto-detect phone/people
        strictMode?: boolean; // Auto-kick on multiple infractions
        exemptInSchool?: boolean; // Allow in-school students to bypass camera
    };
    assignedStudentIds?: string[]; // IDs of specific students assigned to this test (e.g. make-up exams)
}

export interface CBT_Submission {
    id: string; // generated
    testId: string;
    studentId: string;
    schoolId?: string; // Tenant ID — required for scoping teacher/admin result views per-school
    // Denormalized from the CBT_Test at submission time (never looked up separately) —
    // lets firestore.rules scope a teacher's own-test/own-class submission reads via a
    // direct equality check instead of a cross-document "join" through cbt_tests, which
    // rules can't do safely for a `list` request. See the comment on cbt_submissions
    // there for why.
    teacherId?: string;
    classId?: string;
    answers: Record<string, string[] | string>; // questionId -> answer(s)
    score: number;
    totalPoints: number;
    status: 'started' | 'submitted' | 'pending_review' | 'graded';
    startedAt: number;
    submittedAt?: number;
    feedback?: Record<string, string>; // AI Feedback per question
    manualGrades?: Record<string, number>; // questionId -> manual score
    manualFeedback?: Record<string, string>; // questionId -> manual feedback
    overallFeedback?: string;
}

export type InfractionType = 'phone_detected' | 'multiple_persons' | 'tab_switch' | 'fullscreen_exit' | 'no_face' | 'manual_warning';

export interface InvigilationInfraction {
    type: InfractionType;
    timestamp: number;
    snapshotUrl?: string; // Evidence
    cleared?: boolean; // If invigilator overrides
}

export interface InvigilationSession {
    id: string; // matches studentId or testId_studentId
    testId: string;
    studentId: string;
    studentName: string;
    studentPhotoUrl?: string;

    status: 'active' | 'away' | 'locked' | 'kicked' | 'finished' | 'lobby';
    lockedUntil?: number; // If locked, when can they resume?

    lastSnapshotUrl?: string; // Latest webcam frame (base64 or storage URL)
    lastActiveAt: number; // For "Away" detection

    infractions: InvigilationInfraction[];

    // Audio Broadcast Subscriber
    isAudioConnected?: boolean;
}

export interface InvigilationBroadcast {
    id: string;
    testId: string;
    type: 'audio_clip' | 'text_message';
    content: string; // Base64 audio or text
    senderId: string;
    createdAt: number;
}


// --- TIMETABLE & VIRTUAL CLASSROOM TYPES ---

export interface TimetableEntry {
    id: string;
    classId: string;
    subject: string;
    dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 1 = Monday, etc.
    startTime: string; // HH:mm format (e.g., "08:00")
    endTime: string;   // HH:mm format (e.g., "09:00")
    teacherId: string;
    roomId?: string;
    mode?: 'physical' | 'virtual';
    meetingLink?: string;
    recurrence?: 'weekly' | 'once';
    date?: number; // Timestamp for one-off sessions
}

export interface SessionCurriculumTopic {
    id: string;
    title: string;
    subtopics: string[];
    estimatedMinutes?: number;
}

export interface LiveSession {
    id: string;
    schoolId?: string;
    title: string;
    description?: string;
    classId: string;
    teacherId: string;
    topics?: string[];
    startTime: number;
    duration: number;
    meetingLink: string;
    status: 'scheduled' | 'live' | 'ended';
    scheduledFor?: number;
    recurrence?: 'none' | 'daily' | 'weekly';
    recordingUrl?: string;
    endedAt?: number;
    curriculum?: SessionCurriculumTopic[];
    activePresentation?: {
        url: string;
        type: 'pdf' | 'image' | 'pptx';
        title: string;
        resourceId: string;
        /** bytes, when known */
        size?: number;
        /** the page the class is on, and the teacher's zoom and position on it */
        slide?: number;
        zoom?: number;
        vx?: number;
        vy?: number;
    } | null;
    sharedPresentation?: {
        presentationId: string;
        currentSlide: number;
        isActive: boolean;
        slidesJson?: string;
        themeId?: string;
        title?: string;
        /** Live simulator state broadcast by the teacher; read-only for students */
        simulatorState?: Record<string, unknown>;
        /** Student the teacher has called on to answer/interact */
        calledOnStudent?: { userId: string; userName: string; slideIndex: number } | null;
        /** Student quiz answers keyed by userId */
        quizAnswers?: Record<string, { slideIndex: number; answerIndex: number; userName: string }>;
        /** Student the teacher has handed the slides to; they drive Prev/Next until it is taken back */
        presenter?: { identity: string; name: string } | null;
    } | null;
    /** LiveKit identities the teacher has allowed to share their screen in this class */
    allowedSharers?: string[];
    attachedPresentationId?: string;
    recurrenceEndAt?: number;
    weekNumber?: number;
    recurrenceGroupId?: string;
    earlyEntryMinutes?: 0 | 15 | 30 | 60;
}

export interface ClassResource {
    id: string;
    sessionId: string;
    uploaderId: string;
    title: string;
    fileUrl: string;
    fileType: 'image' | 'pdf' | 'audio' | 'doc' | 'link';
    fileName?: string;
    /** bytes, for files uploaded since this was added — Office's viewer refuses anything over 10MB */
    fileSize?: number;
    uploadedAt: number;
}

// Standalone study-materials library — unlike ClassResource, not tied to any single
// live session. classId/sectionId are independent, optional facets (not a strict
// hierarchy): a material can target one specific class, a whole section, or neither
// (school-wide), so each is checked on its own rather than deriving one from the other.
export interface StudyMaterial {
    id: string;
    schoolId: string;
    title: string;
    description?: string;
    category: string;
    classId?: string;
    sectionId?: string;
    session: string;
    term: 'First Term' | 'Second Term' | 'Third Term';
    subject?: string;
    /** Topic covered, so students can find lessons by subject and topic. */
    topic?: string;
    fileUrl: string;
    /** 'doodle' and 'presentation' are AI lessons made in the portal (see refId). */
    fileType: 'image' | 'pdf' | 'audio' | 'doc' | 'video' | 'link' | 'doodle' | 'presentation' | 'cartoon' | 'film';
    /** For doodle / presentation / cartoon / film: the doodle_lessons, presentationDecks, cartoon_lessons or film_lessons document id. */
    refId?: string;
    fileName?: string;
    uploaderId: string;
    uploaderName?: string;
    uploadedAt: number;
}

export interface SchoolExpense {
    id: string;
    schoolId: string;
    category: string;
    amount: number;
    date: number;
    description: string;
    paymentMethod: 'Cash' | 'Bank Transfer' | 'Cheque';
    recordedBy?: string;
    status?: 'Pending' | 'Approved' | 'Rejected';
    approvedBy?: string;
    receiptUrl?: string;
    fileName?: string;
}

export interface OtherIncome {
    id: string;
    schoolId: string;
    description: string;
    amount: number;
    date: number;
    category: string;
    recordedBy: string;
    createdAt: number;
    receiptUrl?: string;
    fileName?: string;
}

export interface Budget {
    id: string;
    category: string;
    amount: number;
    term: number;
    session: string;
    type: 'Income' | 'Expense';
}

export interface Asset {
    id: string;
    name: string;
    category: 'Fixed Asset' | 'Current Asset'; // e.g., Buildings, Furniture, Equipment vs Cash, Inventory
    purchaseDate: number;
    cost: number;
    usefulLifeYears: number; // For depreciation
    currentValue: number; // Calculated or manually updated
    description?: string;
}

export interface Liability {
    id: string;
    name: string;
    category: 'Current Liability' | 'Long-term Liability'; // e.g., Short-term loans vs Mortgages
    amount: number; // Principal amount
    interestRate: number; // Annual %
    startDate: number;
    dueDate?: number;
    description?: string;
    status: 'Active' | 'Paid Off';
}

export interface CapitalTransaction {
    id: string;
    date: number;
    amount: number;
    source: string; // e.g., "Personal Savings", "Investor"
    description?: string;
    type: 'Equity'; // Loans are Liabilities, this is for Equity
}

export interface Notification {
    id: string;
    title: string;
    message: string;
    targetClassId?: string; // Optional, strict target
    type: 'cbt' | 'system' | 'assignment' | 'academic';
    schoolId?: string;
    createdAt: number;
    readBy?: string[];
    link?: string;
    recipientId?: string; // specific user 'all', or classId
}

// --- TAHFIZ MODULE TYPES ---

export type QiraatRiwayah = 'Hafs' | 'Warsh' | 'Qalun' | 'Al-Duri' | 'Hisham' | 'Ibn Dhakwan' | 'Shu\'bah' | 'Khalaf' | 'Khallad' | 'Abu al-Harith' | 'Al-Duri al-Kisa\'i' | 'Ibn Wardan' | 'Ibn Jammaz' | 'Ruways' | 'Rawh' | 'Hafs al-Duri' | 'Ishaq';

export type TajweedRule = 'Ghunnah' | 'Ikhfa' | 'Idgham' | 'Iqlab' | 'Izhar' | 'Qalqalah' | 'Madd Asli' | 'Madd Far\'i' | 'Madd Lazim' | 'Madd Arid' | 'Madd Muttasil' | 'Madd Munfasil' | 'Lam Shamsiyyah' | 'Lam Qamariyyah' | 'Waqf' | 'Sakt' | 'Tafkhim' | 'Tarqiq' | 'Noon Sakinah' | 'Meem Sakinah';

export type MakhrajCategory = 'Al-Jawf' | 'Al-Halq' | 'Al-Lisan' | 'Ash-Shafatan' | 'Al-Khayshum';

export type TahfizGoalPace = 'Quarter Page' | 'Half Page' | 'Full Page' | 'Quarter Juz' | 'Half Juz' | 'Full Juz';

export type TahfizSessionGrade = 'Excellent (A)' | 'Good (B)' | 'Needs Revision (C)' | 'Ungraded';

export type TahfizRetentionLevel = 'Mutqin' | 'Strong' | 'Needs Revision' | 'Critical';

export interface TahfizGoal {
    id: string;
    studentId: string;
    schoolId: string;
    type: 'memorization' | 'review';
    startSurah: number;
    startAyah: number;
    endSurah: number;
    endAyah: number;
    targetCompletionDate: number;
    dailyPace: TahfizGoalPace;
    status: 'active' | 'completed' | 'paused' | 'expired';
    percentComplete: number;
    createdAt: number;
    completedAt?: number;
}

export interface TahfizSession {
    id: string;
    studentId: string;
    schoolId: string;
    date: number;
    durationMinutes: number;
    type: 'memorization' | 'review' | 'recitation';
    surahNumber: number;
    startAyah: number;
    endAyah: number;
    grade: TahfizSessionGrade;
    // AI Auto-grading results
    aiGrading?: {
        overallScore: number; // 0-100
        tajweedScore: number;
        makhaarijScore: number;
        hifdhAccuracy: number;
        fluencyScore: number;
        riwayahCompliance: number;
        detailedMistakes: TahfizMistake[];
        feedback: string;
        gradedAt: number;
    };
    teacherGrade?: TahfizSessionGrade;
    teacherNotes?: string;
    voiceNoteUrl?: string;
    riwayah: QiraatRiwayah;
    /** a practice session with the AI recitation coach */
    coach?: { mode: 'listen' | 'recite' | 'memory'; accuracy: number; words: number; mistakes: number; hints: number; retries: number };
}

export interface TahfizMistake {
    ayahNumber: number;
    wordIndex?: number;
    type: 'tajweed' | 'hifdh' | 'makharij' | 'pronunciation' | 'fluency';
    rule?: TajweedRule;
    makhraj?: MakhrajCategory;
    description: string;
    severity: 'minor' | 'major' | 'critical';
}

export interface TahfizSubmission {
    id: string;
    studentId: string;
    teacherId: string;
    schoolId: string;
    voiceNoteUrl: string;
    voiceNoteDurationSeconds: number;
    surahNumber: number;
    startAyah: number;
    endAyah: number;
    riwayah: QiraatRiwayah;
    status: 'sent' | 'delivered' | 'listened' | 'reviewed' | 'graded';
    submittedAt: number;
    reviewedAt?: number;
    taskId?: string;   // links this submission to a specific TahfizTask
    // AI auto-grade (runs immediately on submission)
    aiGrading?: TahfizSession['aiGrading'];
    // Teacher manual review
    teacherFeedback?: {
        voiceFeedbackUrl?: string;
        grade: TahfizSessionGrade;
        mistakes: TahfizMistake[];
        notes: string;
        reviewedAt: number;
    };
}

export interface TahfizProgress {
    id: string;
    studentId: string;
    schoolId: string;
    totalVersesMemorized: number;
    totalPagesMemorized: number;
    totalPagesReviewed: number;
    currentJuz: number;
    overallAccuracy: number; // 0-100
    retentionLevel: TahfizRetentionLevel;
    juzProgress: Record<number, 'not_started' | 'in_progress' | 'completed'>; // 1-30
    surahProgress: Record<number, { memorized: boolean; lastReviewDate?: number; accuracy?: number }>; // 1-114
    currentStreak: number;
    longestStreak: number;
    totalSessionsCompleted: number;
    totalHoursInvested: number;
    lastActiveAt: number;
    preferredRiwayah: QiraatRiwayah;
    updatedAt: number;
    /** verses the teacher has approved as memorised (graded Excellent or Good): surah → ayah numbers */
    memorisedAyat?: Record<string, number[]>;
}

export interface TahfizBadge {
    id: string;
    studentId: string;
    schoolId: string;
    type: 'streak' | 'juz_complete' | 'surah_complete' | 'accuracy' | 'speed' | 'community' | 'milestone';
    title: string;
    description: string;
    iconEmoji: string;
    unlockedAt: number;
    metadata?: Record<string, any>;
}

export interface TahfizGroup {
    id: string;
    schoolId: string;
    name: string;
    description?: string;
    creatorId: string;
    teacherId?: string;
    memberIds: string[];
    maxMembers: number;
    privacyMode: 'public' | 'private';
    challenge?: {
        title: string;
        targetSurahStart: number;
        targetSurahEnd: number;
        deadline: number;
        completedCount: number;
    };
    createdAt: number;
}

export interface TahfizLeaderboardEntry {
    studentId: string;
    studentName: string;
    rank: number;
    score: number;
    metric: 'verses_memorized' | 'accuracy' | 'streak' | 'engagement';
    period: 'weekly' | 'monthly' | 'alltime';
}

export interface TahfizFeedbackRound {
    round: number;
    submissionId: string;
    voiceNoteUrl: string;
    submittedAt: number;
    reviewedAt: number;
    teacherGrade?: string;
    teacherFeedback?: string;
    teacherAudioFeedbackUrl?: string;
    teacherAiGrading?: any;
}

export interface TahfizTask {
    id: string;
    schoolId: string;
    teacherId: string;
    teacherName: string;
    studentId: string;
    studentName: string;
    surahNumber: number;
    surahName: string;
    fromAyah: number;
    toAyah: number;
    totalAyahs: number;
    notes?: string;
    deadline?: number;
    assignedAt: number;
    status: 'pending' | 'in_progress' | 'submitted' | 'reviewed';
    submissionId?: string;
    submittedAt?: number;
    reviewedAt?: number;
    teacherFeedback?: string;
    teacherGrade?: string;
    teacherAudioFeedbackUrl?: string;
    teacherAiGrading?: any;
    currentRound?: number;
    feedbackHistory?: TahfizFeedbackRound[];
    groupId?: string;    // shared UUID for all tasks created in the same assignment batch
    className?: string;  // class name captured at assignment time
}

// ─── IQA Curriculum Progress ───────────────────────────────────────────────

export interface StudentCurriculumProgress {
    id: string;                       // schoolId_studentId
    studentId: string;
    schoolId: string;
    grade: number;                    // 1-6 (set by teacher/admin)
    memorizationProgram: 'noor' | 'huda';
    noorLevelStatus: Record<number, 'not_started' | 'in_progress' | 'completed'>;  // 1-10
    hudaLevelStatus: Record<number, 'not_started' | 'in_progress' | 'completed'>; // 1-15
    tadabburTopicsCompleted: string[];  // topic IDs
    ethicsTopicsCompleted: string[];
    duasAdhkarItemsCompleted: string[];
    tajweedTopicsCompleted: string[];
    totalHizbCompleted: number;         // 0-30
    completedJuz: number[];
    notes?: string;
    updatedAt: number;
}

// ─── Admissions ────────────────────────────────────────────────────────────
export type AdmissionStatus = 'Pending' | 'PaymentVerified' | 'TestScheduled' | 'Accepted' | 'Rejected';

export interface AdmissionApplication {
    id: string;
    schoolId: string;
    createdAt: number;
    status: AdmissionStatus;
    // Parent / Guardian
    parentName: string;
    parentEmail: string;
    parentPhone: string;
    relationship: 'Father' | 'Mother' | 'Guardian' | 'Other';
    // Child
    childFullName: string;
    childDOB: string;
    childGender: 'Male' | 'Female';
    previousSchool: string;
    // Application
    classAppliedFor: string;
    preferredTestDate: string;
    // Payment
    paymentRef: string;
    paymentVerified: boolean;
    // Admin
    scheduledTestDate?: string;
    adminNotes?: string;
    rejectionReason?: string;
    approvedAt?: number;
    rejectedAt?: number;
}

// ─── Teacher Applications ───────────────────────────────────────────────────
export interface TeacherApplication {
    id?: string;
    fullName: string;
    email: string;
    phone: string;
    qualification: string;
    subjects: string;
    experience: string;
    teachingMode: 'in-person' | 'online' | 'both';
    coverMessage: string;
    schoolId: string;
    schoolName?: string;
    // Compensation
    rateType?: 'hourly' | 'monthly' | 'both';
    currency?: string;
    hourlyRate?: string;
    monthlyRate?: string;
    // Attachments
    cvUrl?: string;
    videoUrl?: string;
    status: 'pending' | 'approved' | 'rejected' | 'interview_scheduled';
    appliedAt: any;
    reviewedAt?: any;
    reviewedBy?: string;
    rejectionReason?: string;
    createdUsername?: string;
    // Interview
    interviewDate?: string;
    interviewMode?: 'in-person' | 'video-call';
    interviewNotes?: string;
    // Assignment (set on approval)
    assignedClassId?: string;
    assignedClassName?: string;
    assignedStudentIds?: string[];
}

// ─── CAMBRIDGE PROGRESSION & CHECKPOINT EXAM SYSTEM ────────────────────────

export type CambridgeExamSubject =
    | 'Mathematics'
    | 'English'
    | 'English (ESL)'
    | 'Science';

export type CambridgeExamVariant = 'progression' | 'checkpoint';

export type CambridgePaperType =
    | 'Reading'
    | 'Writing'
    | 'Listening'
    | 'Non-Calculator'
    | 'Calculator'
    | 'Speaking'
    | 'Combined';

export type CambridgeExamStatus =
    | 'draft'       // Admin editing; hidden from students
    | 'scheduled'   // Window set; not yet open
    | 'open'        // Within scheduledStart–scheduledEnd; students may enter
    | 'closed'      // Past scheduledEnd; awaiting grading
    | 'grading'     // AI Cambridge Marker running or admin reviewing
    | 'released'    // Admin released; students can view + download
    | 'archived';

export type CambridgeAttemptStatus =
    | 'in_progress'
    | 'submitted'
    | 'grading'
    | 'graded'
    | 'released';

export type CambridgeQuestionType =
    | 'multiple_choice'
    | 'short_answer'
    | 'extended_answer'
    | 'gap_fill'
    | 'note_completion'
    | 'labelling';

export type CambridgePerformanceBand =
    | 'Distinction'
    | 'Merit'
    | 'Pass'
    | 'Below Pass';

export interface CambridgeStrandRef {
    id: string;
    label: string;
}

// ── Cambridge Paper-upload supporting types ─────────────────────────────────

export type CambridgeDigitizationStatus =
    | 'none'
    | 'processing'
    | 'review_pending'
    | 'complete';

export interface CambridgeMarkSchemeAnswer {
    acceptedAnswers: string[];
    marksAvailable: number;
    awardingRules: string;
    isConfirmed: boolean;
    /** How this part is marked in the live exam */
    markingMode?: 'auto' | 'ai_draft' | 'manual';
}

export interface CambridgeMediaItem {
    type: 'image' | 'diagram';
    url: string;
    storageRef: string;
    caption?: string;
    extractedFromPage?: number;
}

// ── Core Cambridge interfaces ───────────────────────────────────────────────

export interface CambridgeExam {
    id: string;
    schoolId: string;
    title: string;
    subject: CambridgeExamSubject;
    stage: 3 | 4 | 5 | 6 | 7 | 8 | 9;
    examType: CambridgeExamVariant;
    paperType: CambridgePaperType;
    strands: CambridgeStrandRef[];
    totalMarks: number;
    durationMinutes: number;
    scheduledStart: number;
    scheduledEnd: number;
    status: CambridgeExamStatus;
    createdBy: string;
    createdAt: number;
    updatedAt: number;
    targetClassIds: string[];
    targetStudentIds?: string[]; // if set, also shows for these specific students regardless of class
    instructions?: string;
    // Audio (Listening papers)
    audioUrl?: string;
    audioFileName?: string;
    maxPlays?: number;              // default 2 per Cambridge convention
    // Past paper source
    sourcePastPaperRef?: string;
    sourcePdfUrl?: string;
    sourcePastPaperFileName?: string;
    digitizationStatus?: CambridgeDigitizationStatus;
    digitizationPageRefs?: string[]; // rendered page PNG URLs for admin reference
    digitResultJson?: string; // cached digitized questions & pages pending review (JSON stringified)
    // Mark scheme
    markSchemeUrl?: string;
    markSchemeFileName?: string;
}

// ── Interactive answer zone ───────────────────────────────────────────────────

export interface CambridgeHotspot {
    id: string;
    x: number;              // 0–1 normalised on the question image width
    y: number;              // 0–1 normalised on the question image height
    w: number;
    h: number;
    expectedAnswer?: string;
    label?: string;
}

export type CambridgeAnswerMode = 'overlay' | 'draw' | 'plain';

export interface CambridgeAnswerZone {
    mode: CambridgeAnswerMode;
    hotspots?: CambridgeHotspot[];  // overlay mode: admin-placed typed input boxes
    fieldType?: 'text' | 'number'; // plain mode
    fieldLabel?: string;            // plain mode: optional label above the input
}

// ── Modern CBT answer input types ─────────────────────────────────────────────

export type CambridgeAnswerInputType =
    | 'inline_boxes'       // equation/text with embedded answer boxes
    | 'value_with_unit'    // single number + fixed unit label
    | 'segmented_time'     // hh : mm (or days/hours/minutes)
    | 'tick_grid'          // table — one tick per row
    | 'match_lines'        // two columns tap-to-match
    | 'choice'             // single-selection card list (MCQ)
    | 'drag_tokens'        // tap token → tap zone to place
    | 'draw_canvas'        // freehand drawing over diagram
    | 'open_text'          // multi-line textarea
    | 'ring_select'        // tap to ring/circle one item
    | 'order_sequence'     // arrange items in order
    | 'coordinate'         // ( x , y ) pair
    | 'multi_value'        // N independent answer boxes
    | 'multi_select'       // tick ALL that apply
    | 'sentence_complete'  // running text with in-flow editable blanks (blanks[])
    | 'grid_placement'    // tap tokens into an N×M compass/spatial grid
    | 'digit_cards'       // N single-char digit-tile inputs (e.g. write 3 odd numbers)
    | 'journey_directions' // structured grid journey: direction + squares-count rows
    | 'snap_canvas'       // dot-grid line-drawing for geometric construction
    | 'bar_chart_complete'; // tap columns of a BarChart diagram to draw in the missing bars

export interface CambridgeAnswerConfig {
    // inline_boxes / multi_value: template with [BOX] placeholders
    template?: string;
    // value_with_unit / multi_value: fixed unit
    unit?: string;
    // segmented_time: field descriptors
    timeFields?: Array<{ id: string; label: string; placeholder?: string }>;
    // tick_grid
    gridRows?: Array<{ id: string; label: string }>;
    gridCols?: Array<{ id: string; label: string }>;
    // match_lines
    leftItems?:  Array<{ id: string; label: string }>;
    rightItems?: Array<{ id: string; label: string }>;
    // choice / ring_select / multi_select / order_sequence
    options?: Array<{ id: string; label: string; imgUrl?: string }>;
    // drag_tokens
    tokens?: Array<{ id: string; label: string }>;
    zones?:  Array<{ id: string; label: string }>;
    gridLayout?: {
        rows: number;
        cols: number;
        rowLabels?: string[];
        colLabels?: string[];
    };
    // coordinate axis hints
    xLabel?: string;
    yLabel?: string;
    // multi_value
    boxCount?:   number;
    boxLabels?:  string[];
    // open_text
    rows?:       number;
    wordLimit?:  number;
    // sentence_complete: prose with embedded blanks
    sentenceTemplate?: string; // e.g. "Water boils at __BLANK__ degrees and freezes at __BLANK__."
    // grid_placement: tap tokens into compass/spatial grid
    compassGrid?: {
        rows: number;
        cols: number;
        tokens: Array<{ id: string; label: string }>;
        centerLabel?: string; // label for the immovable centre cell (e.g. "compass")
    };
    // digit_cards: N large single-char tile inputs
    digitCardCount?: number;
    // journey_directions: grid with start/finish + structured direction rows
    journeyGrid?: {
        rows: number;
        cols: number;
        startCell: [number, number];  // [row, col] 0-indexed
        finishCell: [number, number];
    };
    // snap_canvas: dot-grid for geometric construction
    snapCanvas?: {
        gridSize: number;             // number of dots per row/col (e.g. 10)
        starterEdge?: Array<[number, number]>; // pre-drawn vertices [[x,y],[x,y],...]
    };
}

export interface CambridgeQuestion {
    id: string;
    examId: string;
    schoolId: string;
    order: number;
    questionNumber?: string;
    type: CambridgeQuestionType;
    prompt: string;
    partLabel?: string;
    commandWord?: string;
    strandId?: string;
    marks: number;
    markSchemeNotes: string;
    options?: string[];
    gapCount?: number;
    media?: {
        type: 'image' | 'audio' | 'diagram';
        url: string;
        altText?: string;
    };
    // Structured mark scheme (Phase 2c)
    markSchemeAnswers?: CambridgeMarkSchemeAnswer;
    // Additional media items (Phase 2b — extracted from past paper)
    mediaItems?: CambridgeMediaItem[];
    // Digitization review flags
    needsReview?: boolean;
    reviewReason?: string;
    hasImageCue?: boolean;
    // Interactive answer zone (legacy admin-configured)
    answerZone?: CambridgeAnswerZone;
    // Modern CBT answer input type (overrides auto-classification when set)
    answerInputType?: CambridgeAnswerInputType;
    answerConfig?: CambridgeAnswerConfig;
    // Parameterised SVG diagram (replaces screenshot crops for data/structural diagrams)
    diagramType?: string;
    diagramParams?: Record<string, unknown>;
    diagramParamsJson?: string; // serialized JSON to bypass Firestore nested array restriction
    diagramFallbackUrl?: string; // original crop — shown if SVG render fails or type=DecorativeAsset
    diagramAltText?: string;
    validationBypassed?: boolean;
}

export interface CambridgeMarkBreakdown {
    questionId: string;
    strandId: string;
    marksAvailable: number;
    marksAwarded: number;
    feedback: string;
}

export interface CambridgeAttempt {
    id: string;
    examId: string;
    schoolId: string;
    studentId: string;
    answers: Record<string, string>;
    startedAt: number;
    submittedAt?: number;
    status: CambridgeAttemptStatus;
    autoSubmitted: boolean;
    rawScore?: number;
    markBreakdown: CambridgeMarkBreakdown[];
    band?: CambridgePerformanceBand;
    examinerComments?: string;
    reportNarrative?: string;
    gradedBy?: 'ai' | string;
    gradedAt?: number;
    releasedAt?: number;
    releasedBy?: string;
    audioPlays?: number;            // tracks Listening play count for maxPlays enforcement
    extraMinutes?: number;          // admin-granted individual time extension (e.g. for connectivity issues), added on top of exam.scheduledEnd
    retakeScheduledFor?: number;    // admin-granted retake window start (ms timestamp); window runs for exam.durationMinutes from this point
}

// --- HAVE A GO EXERCISES ---
export interface HaveAGoQuestion {
    id: string;
    question: string;
    options: string[];
    correctIndex: number;
    explanation?: string;
}

export interface HaveAGoSubmission {
    id: string;
    schoolId: string;
    studentId: string;
    studentName: string;
    studentAdmNo?: string;
    classId?: string;
    className?: string;
    teacherId?: string;
    presentationId?: string;
    slideIndex?: number;
    topic: string;
    subject?: string;
    questions: HaveAGoQuestion[];
    studentAnswers: Record<string, number>; // questionId -> optionIndex
    status: 'submitted' | 'auto_marked';
    score?: number; // percentage (0..100)
    correctCount?: number;
    totalQuestions: number;
    submittedAt: number;
    markedAt?: number;
    markedBy?: string;
}

